import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import * as dayjs from 'dayjs';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import Helper from '../../helpers/Helper';
import EventsApi from "../../api/instance/EventsApi";
import InstanceApi from "../../api/instance/InstanceApi";
import { getFilterParameters } from '../../helpers/utils';
import { TimeRangeContext, TimeRangeContextType } from "../../context/TimeRangeContext";
import { buildRedirectQuery } from "../../helpers/timeRangeUtils";
import { FilterContext } from "../../context/FilterContext";
import { DATA_INITIALISING, DATA_LOADED, DATA_LOADING } from '../../component/Constants';

// Components
import PeriodBreadcrumb from '../../component/PeriodBreadcrumb';
import Activity from './tabs/Activity';
import ActivityGroupedStatements from './tabs/ActivityGroupedStatements';
import Statistics from './tabs/Statistics';
import Hosts from './tabs/Hosts';
// import Insights from './tabs/Insights';
import Breadcrumb from "../../container/breadcrumb";
import HeaderActions from "../../container/header_actions";
import ConditionalRender from "../../helpers/ConditionalRender";
import FilterBar from "../../component/FilterBar";
import EventHistoryContainer from "../../container/event-history";
import SqlStatistics from "./tabs/SqlStatistics";
import BlockingSessions from './tabs/BlockingSessions/index';
import Deadlocks from './tabs/Deadlocks';
import TempDb from './tabs/TempDb';
import TransactionContention from "./tabs/TransactionContention";
import Parameters from "./tabs/Parameters";
import NotFound from "../NotFound";
import ObjectBrowser from "./tabs/ObjectBrowser/ObjectBrowser";
import AgentJobs from "./tabs/AgentJobs/AgentJobs";

// Types
import InstanceTarget from "../../types/instance/InstanceTarget";
import HostTarget from "../../types/host/HostTarget";
import EventType from "../../types/instance/EventType";
import Alert from "../../types/instance/Alert";
import Event from "../../types/instance/Event";
import Change from "../../types/instance/Change";
import CombinedChanges from "../../types/instance/CombinedChanges";
import ChartPlotLine from "../../types/ChartPlotLine";
import { OverflowTabbedMenu } from "../../container/overflow-tabbed-menu";

dayjs.extend(isoWeek.default);

const Detail = (props: {
    instances: InstanceTarget[],
    hosts: HostTarget[],
    eventTypes: EventType[],
    toggleMenu: Function,
    setFilterOptions: Function
}) => {
    const { instances, hosts, eventTypes, toggleMenu, setFilterOptions } = props
    const { instanceId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<number>(DATA_INITIALISING);
    const [instance, setInstance] = useState<InstanceTarget>();
    const [instanceHosts, setInstanceHosts] = useState<HostTarget[]>([]);
    const [chartPlotLines, setChartPlotLines] = useState<ChartPlotLine[]>([]);
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [events, setEvents] = useState<Event[]>([]);
    const [changes, setChanges] = useState<Change[]>([]);
    const [instanceTypes, setInstanceTypes] = useState<any[]>([]);
    const timeRangeContext = useContext(TimeRangeContext);
    const { currentFilterOptions } = useContext(FilterContext);

    useEffect(() => {
        InstanceApi.getInstanceTypes().then(setInstanceTypes);
    }, []);

    useEffect(() => {
        if (instanceId && timeRangeContext?.timeRange?.from && timeRangeContext?.timeRange?.to) {
            setLoading(DATA_LOADING);
            getData(instanceId);
        }
    }, [instanceId, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    useEffect(() => {
        if (instanceId && instances.length > 0) {
            const filteredInstances = instances.find(instance => Number(instance.id) === Number(instanceId));
            if (filteredInstances) {
                setInstance(filteredInstances);
            }
        }
    }, [instanceId, instances]);

    const getData = async (id: string | undefined) => {
        if (id) {
            const filteredInstances = instances.filter(instance => Number(instance.id) === Number(id));
            if (filteredInstances.length > 0) {
                setInstance(filteredInstances[0]);
            } else {
                navigate(`/not-found`)
            }

            const promiseArray = [getInstanceHosts(), getChanges(timeRangeContext, Number(id))];
            await Promise.all(promiseArray);
            setLoading(DATA_LOADED);
        } else {
            window.location.href = '/not-found';
        }
    };


    const getInstanceHosts = async () => {
        if (!instanceId) return;
        let instanceHostsList: HostTarget[] = [];
        try {
            const hostMappings = await InstanceApi.getInstanceHostByInstance(Number(instanceId));
            for (let index = 0; index < hostMappings.length; index++) {
                const matchedHosts = hosts.filter(host => host.id === hostMappings[index].hostid);
                if (matchedHosts.length === 1) {
                    instanceHostsList.push(matchedHosts[0]);
                }
            }
            setInstanceHosts(instanceHostsList)
        } catch (error) {
            console.error('An error occurred:', error);
        }
    };

    const getChanges = async (timeRangeContext: TimeRangeContextType, instanceId: number) => {
        const allEvents = await EventsApi.getEventsChangesAndAlerts(timeRangeContext, instanceId);

        let combinedChanges: CombinedChanges[] = [],
                chartPlotLines: ChartPlotLine[] = [];

        for (let index = 0; index < allEvents.length; index++) {
            const currentItem = allEvents[index];

            combinedChanges.push({
                id: index,
                instanceId: currentItem.datasourceid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem
            });
        }

        // Set the sort order for the combined changes and events.
        combinedChanges.sort(function compare(a, b) {
            let aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
            let bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
            return bb - aa;
        });

        const interval = Helper.getInterval(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, timeRangeContext.timeRange?.interval)
        const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, interval)
        chartPlotLines = Helper.getChartPlotLines(buildRedirectQuery(timeRangeContext.timeRange), combinedChanges, chartCategories, interval, false, undefined, eventTypes);
        const alerts = allEvents.filter((e: any) => e.event === 'alert').map((e: any) => ({ ...e, alertType: e.name, severity: e.severity }))
        const events = allEvents.filter((e: any) => e.event === 'custom')
        const changes = allEvents.filter((e: any) => e.event === 'change')

        setAlerts(alerts);
        setEvents(events);
        setChanges(changes);
        setChartPlotLines(chartPlotLines);
    };

    const refresh = () => {
        setLoading(DATA_LOADING);
        void getData(instanceId);
    };

    const instanceType = instance?.type || '';

    // Tab visibility based on instance type — matches the settings page logic
    const isTabSupported = useMemo(() => ({
        sqlStatistics: instanceType !== 'generic',
        blockingSessions: instanceType !== 'cockroachdb',
        deadlocks: instanceType === 'oracle' || instanceType === 'sqlserver',
        tempdb: instanceType === 'sqlserver',
        transactionContention: instanceType === 'cockroachdb',
        objectStatistics: true,
        parameters: true,
        agentJobs: instanceType === 'sqlserver',
    }), [instanceType]);

    const navItems = useMemo(() => [
        {
            label: 'Activity',
            icon: 'fal fa-chart-pie-alt',
            path: 'activity',
            show: true,
            end: true,
            className: '',
        },
        {
            label: 'Grouped Activity',
            icon: 'fal fa-chart-pie-alt',
            path: 'activity/groupedstatements',
            show: true,
            end: false,
            className: '',
        },
        {
            label: 'SQL Statistics',
            icon: 'fal fa-chart-bar',
            path: 'sql-statistics',
            show: isTabSupported.sqlStatistics,
            end: false,
            className: '',
        },
        {
            label: 'Statistics',
            icon: 'fal fa-chart-bar',
            path: 'statistics',
            show: true,
            end: false,
            className: '',
        },
        {
            label: (
                    <>
                        Host Metrics
                        <span className="badge bg-info" data-tooltip-id="global-tooltip" data-tooltip-content="Total host count">
          {instanceHosts.length}
        </span>
                    </>
            ),
            icon: 'fal fa-server',
            path: 'hosts',
            show: true,
            end: false,
            className: '',
        },
        {
            label: 'Events',
            icon: 'fal fa-exchange',
            path: 'events',
            show: true,
            end: false,
            className: '',
        },
        {
            label: 'Blocking Sessions',
            icon: 'fal fa-lock',
            path: 'blocking-sessions',
            show: isTabSupported.blockingSessions,
            end: false,
            className: '',
        },
        {
            label: 'Deadlocks',
            icon: 'fal fa-layer-group',
            path: 'deadlocks',
            show: isTabSupported.deadlocks,
            end: false,
            className: '',
        },
        {
            label: 'TempDB',
            icon: 'fal fa-database',
            path: 'tempdb',
            show: isTabSupported.tempdb,
            end: false,
            className: '',
        },
        {
            label: 'Transaction Contention',
            icon: 'fal fa-clock',
            path: 'transaction-contention',
            show: isTabSupported.transactionContention,
            end: false,
            className: '',
        },
        {
            label: 'Parameters',
            icon: 'fal fa-computer-classic',
            path: 'parameters',
            show: isTabSupported.parameters,
            end: false,
            className: '',
        },
        {
            label: 'Object Browser',
            icon: 'fal fa-table',
            path: 'object-browser',
            show: isTabSupported.objectStatistics,
            end: false,
            className: '',
        },
        {
            label: 'Agent Jobs',
            icon: 'fal fa-history',
            path: 'agent-jobs',
            show: isTabSupported.agentJobs,
            end: false,
            className: '',
        },
    ], [instanceHosts, instance, isTabSupported]);


    if (!instance) {
        return <React.Fragment>...</React.Fragment>;
    }

    return (
            <div id="content">
                <Helmet>
                    <title>{instance.name} - DBmarlin</title>
                    <meta name="description"
                          content="Instance performance detail view, showing key performance metrics and detailed executed statement and waits information"/>
                </Helmet>

                <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                    <Breadcrumb instanceId={instance.id} type="instances" heading={instance.name}>
                        <Link to="/instances">Analysis</Link>
                        <Link to="/instances">Instances</Link>
                        <Link to={`/instances/${instance.id}/activity`}>{instance.name}</Link>
                        <PeriodBreadcrumb/>
                    </Breadcrumb>
                    <HeaderActions toggleMenu={toggleMenu} refresh={refresh} setFilterOptions={setFilterOptions}/>
                </div>

                <div className="loader">
                    <ConditionalRender if={loading === DATA_LOADING}>
                        <div className="bar"/>
                    </ConditionalRender>
                </div>

                <ConditionalRender if={setFilterOptions}>
                    <FilterBar setFilterOptions={setFilterOptions}/>
                </ConditionalRender>

                <OverflowTabbedMenu items={navItems}/>

                <Routes>
                    {/*<Route path="/"*/}
                    {/*       element={<Activity period={period} alerts={alerts} instance={instance} hosts={instanceHosts}*/}
                    {/*                                filterOptions={period.filters.options}*/}
                    {/*                                filterParameters={period.filters.parameters}*/}
                    {/*                                chartPlotBands={chartPlotBands} chartPlotLines={chartPlotLines}*/}
                    {/*                                changes={changes} events={events}*/}
                    {/*                               */}
                    {/*                                setFilterOptions={setFilterOptions}/>}/>*/}
                    <Route path="/activity/*"
                           element={<Activity alerts={alerts} instance={instance} hosts={instanceHosts}
                                              instanceId={Number(instanceId)}
                                              filterOptions={currentFilterOptions}
                                              filterParameters={getFilterParameters(currentFilterOptions)}
                                              chartPlotLines={chartPlotLines}
                                              changes={changes} events={events}
                                             
                                              setFilterOptions={setFilterOptions}/>}/>
                    <Route path="/activity/groupedstatements"
                           element={<ActivityGroupedStatements alerts={alerts} instance={instance} hosts={instanceHosts}
                                                               filterOptions={currentFilterOptions}
                                                               filterParameters={getFilterParameters(currentFilterOptions)}
                                                               instanceId={Number(instanceId)}
                                                               chartPlotLines={chartPlotLines} changes={changes}
                                                               events={events}
                                                              
                                                               setFilterOptions={setFilterOptions}/>}/>
                    <Route path="/statistics"
                           element={<Statistics instance={instance}
                                               />}/>
                    <Route path="/hosts"
                           element={<Hosts instance={instance} hosts={instanceHosts} chartPlotLines={chartPlotLines}

                                          />}/>
                    <Route path="/sql-statistics"
                           element={<SqlStatistics chartPlotLines={chartPlotLines}
                                                   changes={changes} events={events} alerts={alerts}
                                                   instanceTypes={instanceTypes}
                                                   instance={instance}/>}/>
                    {/*<Route path="/insights" element={<Insights/>}/>*/}
                    <Route path="/events"
                           element={<EventHistoryContainer instance={instance}

                                                           eventTypes={eventTypes}/>}/>
                    <Route path="/blocking-sessions"
                           element={<BlockingSessions instance={instance}
                                                     instanceTypes={instanceTypes}
                           />}/>
                    <Route path="/transaction-contention"
                           element={<TransactionContention instance={instance}
                                                          instanceTypes={instanceTypes}
                           />}/>
                    <Route path="/deadlocks"
                           element={<Deadlocks instance={instance} setFilterOptions={setFilterOptions}
                                               instanceTypes={instanceTypes}
                                               filterOptions={currentFilterOptions}/>}/>
                    <Route path="/tempdb"
                           element={<TempDb instance={instance} setFilterOptions={setFilterOptions}
                                            instanceTypes={instanceTypes}
                                            filterOptions={currentFilterOptions}
                                           />}/>

                    <Route path="/parameters"
                           element={<Parameters instance={instance}
                                                instanceTypes={instanceTypes}
                                               />}/>

                    <Route path="/object-browser" element={<ObjectBrowser instance={instance} instanceTypes={instanceTypes}/>}/>
                    <Route path="/agent-jobs"
                           element={<AgentJobs instance={instance}
                                                  instanceTypes={instanceTypes}
                                                  chartPlotLines={chartPlotLines}
                                                  changes={changes}
                                                  events={events}
                                                  alerts={alerts}
                                                 />}/>
                    <Route path="*" element={<NotFound/>}/>
                </Routes>
            </div>
    );
};

Detail.propTypes = {
    instances: PropTypes.array.isRequired,
    hosts: PropTypes.array.isRequired,
    eventTypes: PropTypes.array,
    toggleMenu: PropTypes.func.isRequired,
    setFilterOptions: PropTypes.func.isRequired
};

export default Detail;
